/**
 * Skeleton + Spinner primitives — TASK 7.1.
 *
 * SKELETON — the mock's three loading placeholders, verbatim:
 *   - `list`  (L110): 64px tall, 14px radius, 3px ink border, sheening
 *                     `linear-gradient(90deg,#241B33,#2E2342,#241B33)`;
 *   - `cover` (L155): 190px tall rail card, flat #241B33 fill;
 *   - `row`   (L188): 60px tall, 12px radius, flat #241B33 fill.
 * All three carry `animation:smSheen 1.4s ease infinite`, which under
 * `prefers-reduced-motion: reduce` is switched off by motion.css.
 *
 * SPINNER — the mock's five ring spinners (L34 34px teal / L47 20px yellow /
 * L361 18px pink / L718 16px yellow / L741 40px yellow), each a bordered
 * circle with a transparent top edge spinning via `smSpin`.
 *
 * Both are decorative wrappers around a live region: the mock puts
 * `role="status"` + `aria-label` on the CONTAINER (L108, L154, L188, L34),
 * so `SkeletonList()`/`Spinner()` reproduce that rather than putting the role
 * on every shimmer block — otherwise a five-item skeleton would announce
 * "Loading" five times.
 *
 * @module
 */

import { append, classes, el } from './dom.js';

const SKELETON_VARIANT = {
  list: undefined,
  cover: 'sm-skeleton--cover',
  row: 'sm-skeleton--row',
};

/**
 * A single shimmer block.
 *
 * @param {{ variant?: keyof typeof SKELETON_VARIANT, testId?: string }} [options]
 * @returns {HTMLElement}
 */
export function Skeleton(options = {}) {
  const { variant = 'list', testId } = options;
  if (!(variant in SKELETON_VARIANT)) {
    throw new Error(`Skeleton: unknown variant "${String(variant)}"`);
  }
  return el('div', {
    className: classes('sm-skeleton', SKELETON_VARIANT[variant], 'sm-anim-sheen'),
    testId,
    attrs: { 'aria-hidden': 'true' },
  });
}

/**
 * `count` shimmer blocks inside the mock's announcing container
 * (mock L108/L154/L188: `role="status" aria-label="Loading…"`).
 *
 * @param {{
 *   count?: number,
 *   variant?: keyof typeof SKELETON_VARIANT,
 *   label?: string,
 *   layout?: 'stack'|'rail',
 *   testId?: string,
 * }} [options]
 * @returns {HTMLElement}
 */
export function SkeletonList(options = {}) {
  const { count = 5, variant = 'list', label = 'Loading', layout = 'stack', testId } = options;
  const root = el('div', {
    className: classes(
      layout === 'rail' ? 'sm-rail' : variant === 'row' ? 'sm-stack-10' : 'sm-stack-10',
      layout === 'rail' && 'sm-rail--skeleton',
    ),
    testId,
    attrs: { role: 'status', 'aria-label': label },
  });
  for (let i = 0; i < count; i += 1) {
    append(root, Skeleton({ variant }));
  }
  return root;
}

const SPINNER_SIZE = {
  /** mock L34 — cold boot, 34px teal, `smSpin 1s` */
  boot: { className: undefined, anim: 'sm-anim-spin' },
  /** mock L47 — sign-in in flight, 20px yellow, `smSpin .9s` */
  signin: { className: 'sm-spinner--20', anim: 'sm-anim-spin-mid' },
  /** mock L361 — search, 18px pink, `smSpin .8s` */
  search: { className: 'sm-spinner--18', anim: 'sm-anim-spin-fast' },
  /** mock L718 — mini-player buffering, 16px yellow, `smSpin .8s` */
  mini: { className: 'sm-spinner--16', anim: 'sm-anim-spin-fast' },
  /** mock L741 — full-player buffering, 40px yellow, `smSpin .8s` */
  player: { className: 'sm-spinner--40', anim: 'sm-anim-spin-fast' },
};

/**
 * @param {{
 *   size?: keyof typeof SPINNER_SIZE,
 *   label?: string,
 *   announce?: boolean,
 *   testId?: string,
 * }} [options]
 * @returns {HTMLElement}
 */
export function Spinner(options = {}) {
  const { size = 'boot', label = 'Loading', announce = true, testId } = options;
  const preset = SPINNER_SIZE[size];
  if (preset === undefined) throw new Error(`Spinner: unknown size "${String(size)}"`);

  /** @type {Record<string, string|number|boolean|null|undefined>} */
  const attrs = announce ? { role: 'status', 'aria-label': label } : { 'aria-hidden': 'true' };

  return el('div', {
    className: classes('sm-spinner', preset.className, preset.anim),
    testId,
    attrs,
  });
}
